import { useState, type ReactNode } from "react";
import {
  File,
  FileArchive,
  FileText,
  Image as ImageIcon,
  Lock,
  Reply,
} from "lucide-react";
import { useTranslation } from "react-i18next";

import { Badge } from "@lytra/frontend-common/components/v2/ui/badge";
import { Button } from "@lytra/frontend-common/components/v2/ui/button";
import type { IconComponent } from "@lytra/frontend-common/components/v2/ui/icon";
import CollapsibleItems from "./CollapsibleItems";
import { ClampedText } from "./ClampedText";
import { ThreadCard } from "./ThreadCard";
import MessageContent from "./MessageContent";
import { formatFileSize } from "./ticketHelpers";
import type { TicketMessage } from "./noteThreads";

/** What the row reads of a message; every `TicketMessage` fits. */
export type ForwardReplyMessage = Pick<
  TicketMessage,
  "id" | "contentText" | "receivedAt" | "sentAt" | "createdAt"
> &
  Partial<
    Pick<
      TicketMessage,
      | "contentHtml"
      | "emailFullContentText"
      | "emailFullContentHtml"
      | "emailFromAddress"
      | "emailFromName"
      | "attachments"
    >
  >;

interface ForwardReplyRowProps {
  message: ForwardReplyMessage;
  /**
   * Opens the thread's internal reply composer (D-13). Without it the row has no "Antworten"
   * (dialogs, archive views): a mail reply can only ever be answered inside Lytra.
   */
  onReply?: () => void;
  /** Draws the deep-link ring around this reply. */
  highlighted?: boolean;
  /** Shows the whole message instead of the clamp; set for a deep-linked reply. */
  expanded?: boolean;
  /** Downloads an attachment; without it the attachments are listed but not clickable. */
  onDownloadAttachment?: (attachmentId: string) => void;
  /**
   * The line that ties the reply to its share, above the body. With it the row is a reply of
   * its own in the thread and carries the dark "Intern" badge.
   */
  quote?: ReactNode;
}

/** The dark "Intern" badge of a reply to a share, so it never reads as a mail to the customer. */
export function InternalReplyBadge() {
  const { t } = useTranslation();
  return (
    <Badge size="xs" icon={Lock} className="shrink-0 bg-ink text-card">
      {t("tickets.forward.reply.internal")}
      <span className="sr-only">{t("tickets.forward.entry.internalSr")}</span>
    </Badge>
  );
}

/** The v2 counterpart of the ticket's `getAttachmentIcon`: a component, so the Button draws it at 1.75. */
function attachmentIcon(contentType: string): IconComponent {
  if (contentType.startsWith("image/")) return ImageIcon;
  if (
    contentType.startsWith("text/") ||
    contentType.startsWith("application/pdf")
  )
    return FileText;
  if (contentType.includes("zip") || contentType.includes("compressed"))
    return FileArchive;
  return File;
}

const blank = (value: string | null | undefined): boolean =>
  !value || value.trim() === "";

/**
 * A colleague's mail reply to a share (#717, D-12, D-13, UI-SPEC 5): a reply of its own in the
 * thread, told apart from a reply in lytra by the "per Mail" badge. The
 * body is the ticket's own mail rendering (sanitized, remote images off by default) with its
 * quoted-history toggle; the only action is an internal "Antworten". A mail reply is never
 * edited, deleted, answered to the customer, nor does it show a delivery state or a draft.
 */
export function ForwardReplyRow({
  message,
  onReply,
  highlighted = false,
  expanded = false,
  onDownloadAttachment,
  quote,
}: ForwardReplyRowProps) {
  const { t } = useTranslation();
  const [showFullContent, setShowFullContent] = useState(false);
  const [loadRemoteImages, setLoadRemoteImages] = useState(false);

  const address = message.emailFromAddress?.trim() || null;
  const displayName = message.emailFromName?.trim() || null;
  // A reply without a display name shows its address, never a blank header (E5).
  const name = displayName ?? address ?? t("tickets.meta.unknown");
  const showAddress = address !== null && displayName !== null && displayName !== address;
  const time = message.receivedAt ?? message.sentAt ?? message.createdAt;

  const hasHistory =
    !blank(message.emailFullContentText) || !blank(message.emailFullContentHtml);
  const noNewText =
    blank(message.contentText) && blank(message.contentHtml);
  // New text empty: say so and keep the history one click away, instead of a bare placeholder.
  const historyOnly = noNewText && hasHistory && !showFullContent;

  const attachments = (message.attachments?.edges ?? [])
    .map(({ node }) => node)
    .filter((node) => !node.isInline);

  return (
    <ThreadCard
      messageId={message.id}
      ringed={highlighted}
      name={name}
      avatarSize="xs"
      aside={showAddress ? address : null}
      time={time}
      trailing={
        <>
          <Badge tone="neutral" size="xs" className="ml-auto shrink-0">
            {t("tickets.forward.reply.byMail")}
          </Badge>
          {quote ? <InternalReplyBadge /> : null}
        </>
      }
    >
      {quote}
      {historyOnly ? (
        <div className="flex min-w-0 flex-col items-start gap-1">
          <p className="text-caption text-ink-muted">
            {t("tickets.forward.reply.noNewText")}
          </p>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            aria-expanded={false}
            onClick={() => setShowFullContent(true)}
          >
            {t("tickets.forward.reply.showHistory")}
          </Button>
        </div>
      ) : (
        <ClampedText
          clamp={!expanded}
          textKey={`${message.contentText ?? ""}${message.emailFullContentText ?? ""}${showFullContent}`}
        >
          <MessageContent
            emailIsForwarded={false}
            emailHasForwarderContent={false}
            content={{
              text: message.contentText ?? null,
              html: message.contentHtml ?? null,
              emailFullText: message.emailFullContentText ?? null,
              emailFullHtml: message.emailFullContentHtml ?? null,
              emailSubject: null,
              emailForwardedSubject: null,
              emailForwardedContentText: null,
              languageCode: null,
            }}
            showFullContent={showFullContent}
            onToggleContent={() => setShowFullContent((value) => !value)}
            loadRemoteImages={loadRemoteImages}
            onToggleRemoteImages={() => setLoadRemoteImages((value) => !value)}
          />
        </ClampedText>
      )}
      {attachments.length > 0 && (
        <div className="flex min-w-0 flex-col gap-1">
          <span className="text-caption text-ink-muted">
            {t("tickets.forward.reply.attachments")}
          </span>
          <div className="flex flex-wrap gap-2">
            <CollapsibleItems items={attachments}>
              {(visible) =>
                visible.map((attachment) => (
                  <Button
                    key={attachment.id}
                    type="button"
                    variant="outline"
                    size="sm"
                    leadingIcon={attachmentIcon(attachment.contentType)}
                    disabled={!onDownloadAttachment}
                    onClick={() => onDownloadAttachment?.(attachment.id)}
                    // Button is shrink-0: a long file name must truncate, not widen the row.
                    className="min-w-0 max-w-full shrink"
                  >
                    {attachment.name}{" "}
                    <span className="font-normal text-ink-muted">
                      ({formatFileSize(attachment.size)})
                    </span>
                  </Button>
                ))
              }
            </CollapsibleItems>
          </div>
        </div>
      )}
      {onReply && (
        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            leadingIcon={Reply}
            onClick={onReply}
          >
            {t("tickets.notes.reply")}
          </Button>
        </div>
      )}
    </ThreadCard>
  );
}
